import { DeveloperGame, Submission } from '../../submission-graphql';
import {
  SubmissionOverviewHeaderBadges,
  SubmissionOverviewHeaderContainer,
  SubmissionOverviewHeaderCoverImg,
  SubmissionOverviewHeaderMetadata,
  SubmissionOverviewHeaderTitle,
} from './submissionOverviewHeader.styles';
import React from 'react';
import { basicLaunchEndLabel, hadBasicLaunch, readableStatus } from '../../../../../../../common/domain/submission';
import { submissionHadBasicLaunch, submissionHasBasicLaunchExpired } from '../../../../../../helpers/flowHelpers';
import moment from 'moment';
import { prefixGraphUrl } from '../../../../../../../common/util';
import ToggleIndicator from '../../../../../../../common/Styleguide/Common/ToggleIndicator';
import Pill from '../../../../../../../common/Styleguide/Common/Pill';
import { getStatusColor } from '../../../Games/GameList/gamesList.helper';
import { getGameUrl } from '../../../../../../../common/domain-names';
import { COLORS } from '../../../../../../../common/Styleguide/Common/colors';
import SubmissionNewBuildButton from './SubmissionNewBuildButton';
import ReleaseSubmissionButton from './ReleaseSubmissionButton';
import SubmissionDeleteButton from './SubmissionDeleteButton';

interface SubmissionOverviewHeaderProps {
  submission: Submission;
  game: DeveloperGame | undefined;
  goToInfoUpdate(): void;
  createNewGameBuild(): void;
}

const SubmissionOverviewHeader: React.FC<SubmissionOverviewHeaderProps> = ({ submission, game, createNewGameBuild }) => {
  const SUBMISSION_GAME_NAME = submission.gameName;
  const SUBMISSION_STATUS = submission.status;

  // thumb
  const gameCover16x9 = submission.gameCovers?.find((gameCover) => gameCover.size === '16x9');
  const coverImageUrl = gameCover16x9 && gameCover16x9.uploadedFile ? gameCover16x9.uploadedFile.temporaryUrl : null;
  const coverImgSrc = coverImageUrl && prefixGraphUrl(coverImageUrl);

  // badges
  const submissionStatusLabel = readableStatus(SUBMISSION_STATUS);
  const submissionStatusColor = getStatusColor(SUBMISSION_STATUS);
  const gameUrl = {
    href: getGameUrl(submission.slug, { cacheBust: true }),
    target: '_blank',
    rel: 'noopener noreferrer',
  };
  const showPortalLink =
    !['REJECTED', 'FINALIZED'].includes(submission.status) &&
    (submission.status === 'RELEASED' || submissionHadBasicLaunch(submission.statusChanges));

  // flow
  const getActiveFlowValue = () => {
    if (submission.gameJamId) {
      return 'gameJam';
    }
    if (submission.isFullLaunch) {
      return 'full';
    }
    return 'basic';
  };
  const flowValues = submission.gameJamId
    ? [{ label: 'Game Jam', id: 'gameJam' }]
    : [
        { label: 'Basic', id: 'basic' },
        { label: 'Full', id: 'full' },
      ];

  // dates
  const { endsOn, hasExpired } = submissionHasBasicLaunchExpired(submission);
  const formatDate = (date: string): string => {
    return moment(date).format('DD.MM.YYYY');
  };

  // ctas
  const shouldDisplayReleaseButton = submission && submission.status === 'SYNC_REQUIRED' && submission.developer?.hasReleaseRights;

  return (
    <SubmissionOverviewHeaderContainer>
      <div>
        <div>
          {coverImgSrc && (
            <SubmissionOverviewHeaderCoverImg>
              <img src={coverImgSrc} alt={`${SUBMISSION_GAME_NAME}_cover`} />
            </SubmissionOverviewHeaderCoverImg>
          )}
          <div>
            <SubmissionOverviewHeaderTitle>{SUBMISSION_GAME_NAME}</SubmissionOverviewHeaderTitle>
            <SubmissionOverviewHeaderBadges>
              <Pill label={submissionStatusLabel} backgroundColor={submissionStatusColor} color={COLORS.black[90]} variant="primary" />
              {showPortalLink && (
                <Pill label="View game" backgroundColor={COLORS.black[10]} color={COLORS.white[100]} variant="secondary" link={gameUrl} />
              )}
            </SubmissionOverviewHeaderBadges>
          </div>
        </div>
        <ToggleIndicator values={flowValues} activeValue={getActiveFlowValue()} />
      </div>
      <div>
        <div>
          {submission.submittedOn && (
            <SubmissionOverviewHeaderMetadata>
              Submission date: <span>{formatDate(submission.submittedOn)}</span>
            </SubmissionOverviewHeaderMetadata>
          )}
          {game?.addedOn && (
            <SubmissionOverviewHeaderMetadata>
              Published date: <span>{formatDate(game.addedOn)}</span>
            </SubmissionOverviewHeaderMetadata>
          )}
          {submission.basicLaunchOn && hadBasicLaunch(submission.status) && (
            <SubmissionOverviewHeaderMetadata>
              Basic Launch started on: <span>{formatDate(submission.basicLaunchOn)}</span>
            </SubmissionOverviewHeaderMetadata>
          )}
          {submissionHadBasicLaunch(submission.statusChanges) && endsOn && (
            <SubmissionOverviewHeaderMetadata>
              {basicLaunchEndLabel(hasExpired)}: <span>{formatDate(endsOn)}</span>
            </SubmissionOverviewHeaderMetadata>
          )}
          <SubmissionOverviewHeaderMetadata>
            Category: <span>{submission.categoryName}</span>
          </SubmissionOverviewHeaderMetadata>
        </div>
        <div>
          <SubmissionNewBuildButton createNewGameBuild={createNewGameBuild} />
          {shouldDisplayReleaseButton && <ReleaseSubmissionButton submission={submission} />}
          <SubmissionDeleteButton />
        </div>
      </div>
    </SubmissionOverviewHeaderContainer>
  );
};

export default SubmissionOverviewHeader;
